import { IconEdit, IconPlus, IconTrash } from "@tabler/icons-react";
import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useCollectionPromotions,
  useDeleteCollectionPromotion,
  useUpdateCollectionPromotion,
} from "@/hooks/use-menu";
import { useActiveOrganization } from "@/hooks/use-organizations";
import type { MenuCollectionPromotion } from "@/types/menu";

const formatDate = (value: string) => {
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(new Date(value));
};

export default function CollectionPromotionsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: activeOrganization, isLoading: isLoadingOrganization } = useActiveOrganization();
  const { data, error, isLoading } = useCollectionPromotions(activeOrganization?.id);
  const updatePromotion = useUpdateCollectionPromotion();
  const deletePromotion = useDeleteCollectionPromotion();

  const handleDelete = (promotionId: string) => {
    deletePromotion.mutate(promotionId, {
      onError: (mutationError) => {
        toast.error(mutationError.message);
      },
      onSuccess: () => {
        toast.success(t("menu.promotions.shared.notifications.deleted"));
      },
    });
  };

  const handleActiveChange = (promotion: MenuCollectionPromotion, active: boolean) => {
    updatePromotion.mutate(
      {
        data: { active },
        id: promotion.id,
      },
      {
        onError: (mutationError) => {
          toast.error(mutationError.message);
        },
      },
    );
  };

  if (isLoadingOrganization) {
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!activeOrganization) {
    return (
      <div className="p-6">
        <Card>
          <CardHeader>
            <CardTitle>{t("menu.promotions.collection.title")}</CardTitle>
            <CardDescription>
              {t("menu.promotions.shared.notifications.activeOrgRequired")}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  let promotionsContent: ReactNode = (
    <div className="rounded-lg border border-dashed p-8 text-center">
      <h2 className="font-medium text-base">{t("menu.promotions.collection.table.emptyTitle")}</h2>
      <p className="mt-2 text-muted-foreground text-sm">
        {t("menu.promotions.collection.table.emptyDescription")}
      </p>
    </div>
  );

  if (isLoading) {
    promotionsContent = (
      <div className="space-y-3">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  } else if (error) {
    promotionsContent = <p className="text-destructive text-sm">{error.message}</p>;
  } else if (data?.promotions.length) {
    promotionsContent = (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("menu.promotions.shared.table.name")}</TableHead>
            <TableHead>{t("menu.promotions.shared.table.discount")}</TableHead>
            <TableHead>{t("menu.promotions.shared.table.active")}</TableHead>
            <TableHead>{t("menu.promotions.shared.table.startDate")}</TableHead>
            <TableHead>{t("menu.promotions.shared.table.endDate")}</TableHead>
            <TableHead className="text-right">
              {t("menu.promotions.shared.table.actions")}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.promotions.map((promotion) => (
            <TableRow key={promotion.id}>
              <TableCell>
                <p className="font-medium">{promotion.name}</p>
              </TableCell>
              <TableCell className="text-sm">{promotion.discount}%</TableCell>
              <TableCell>
                <Switch
                  checked={promotion.active}
                  onCheckedChange={(checked) => handleActiveChange(promotion, checked)}
                />
              </TableCell>
              <TableCell className="text-muted-foreground text-sm">
                {promotion.startDate ? formatDate(promotion.startDate) : "-"}
              </TableCell>
              <TableCell className="text-muted-foreground text-sm">
                {promotion.endDate ? formatDate(promotion.endDate) : "-"}
              </TableCell>
              <TableCell>
                <div className="flex justify-end gap-2">
                  <Button
                    onClick={() => navigate(`/menu/promotions/collection/${promotion.id}/edit`)}
                    size="icon"
                    type="button"
                    variant="outline"
                  >
                    <IconEdit className="size-4" />
                    <span className="sr-only">{t("menu.promotions.shared.actions.edit")}</span>
                  </Button>
                  <Button
                    onClick={() => handleDelete(promotion.id)}
                    size="icon"
                    type="button"
                    variant="outline"
                  >
                    <IconTrash className="size-4" />
                    <span className="sr-only">{t("menu.promotions.shared.actions.delete")}</span>
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  return (
    <div className="flex flex-col gap-8 p-6">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="font-serif font-semibold text-2xl tracking-tight">
            {t("menu.promotions.collection.title")}
          </h1>
          <p className="text-muted-foreground text-sm">
            {t("menu.promotions.collection.description", {
              organization: activeOrganization.name,
            })}
          </p>
        </div>
        <Button onClick={() => navigate("/menu/promotions/collection/new")} type="button">
          <IconPlus className="size-4" />
          {t("menu.promotions.collection.newButton")}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="font-serif">
            {t("menu.promotions.collection.table.title")}
          </CardTitle>
          <CardDescription>{t("menu.promotions.collection.table.description")}</CardDescription>
        </CardHeader>
        <CardContent>{promotionsContent}</CardContent>
      </Card>
    </div>
  );
}
